import React, { useState, useEffect, useCallback } from 'react';
import Stamp from '../components/layout/Stamp';
import { apiService } from '../services/apiService';

interface AchievementDocument {
  id: string;
  url: string;
  fileName: string;
  uploadedAt: string;
  status: string;
}

interface QueueAchievement {
  id: string;
  title: string;
  category: string;
  year: number | null;
  rank: string | null;
  activityTier: string | null;
  verificationStatus: string;
  verificationRecordId: string | null;
  verifiedAt: string | null;
  documents: AchievementDocument[];
}

interface Applicant {
  profileId: string;
  displayName: string;
  email: string | null;
  university: string | null;
  classYear: string | null;
  mentorType: string;
  occupation: string | null;
  isVerified: boolean;
  pricePerSession: number;
  appliedAt: string;
  achievements: QueueAchievement[];
}

interface WaitlistSegment {
  segment: string;
  count: number;
}

interface WaitlistEntryRow {
  id: string;
  email: string;
  segment: string;
  mentorType: string;
  note: string | null;
  status: string;
  createdAt: string;
  user?: { id: string; name: string | null; email: string } | null;
}

const TIER_LABELS: Record<string, string> = {
  state: 'State · $85/hr',
  top300: 'National top-300 · $110/hr',
  championship: 'Championship · $150/hr',
};

const STATUS_META: Record<string, { variant: 'verified' | 'selfrep'; label: string }> = {
  verified: { variant: 'verified', label: 'Verified' },
  pending: { variant: 'selfrep', label: 'Pending review' },
  self_reported: { variant: 'selfrep', label: 'Self-reported' },
  rejected: { variant: 'selfrep', label: 'Rejected' },
};

const OpsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [segments, setSegments] = useState<WaitlistSegment[]>([]);
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntryRow[]>([]);
  const [waitlistTotal, setWaitlistTotal] = useState(0);
  const [actingOn, setActingOn] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    const [queue, waitlist] = await Promise.all([
      apiService.getVerificationQueue(),
      apiService.getAdminWaitlist(),
    ]);
    if (!queue.success && !waitlist.success) {
      setDenied(true);
      setLoading(false);
      return;
    }
    setApplicants(queue.applicants || []);
    setPendingCount(queue.pendingCount || 0);
    setSegments(waitlist.segments || []);
    setWaitlistEntries(waitlist.entries || []);
    setWaitlistTotal(waitlist.total || 0);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (
    achievement: QueueAchievement,
    action: 'verify' | 'self_report' | 'reject'
  ) => {
    if (actingOn) return;
    let recordId: string | undefined;
    if (action === 'verify') {
      const input = window.prompt('Official record ID (optional):', '');
      if (input === null) return; // cancelled
      recordId = input.trim() || undefined;
    }
    if (action === 'reject' && !window.confirm(`Reject claim "${achievement.title}"?`)) {
      return;
    }
    setActingOn(achievement.id);
    await apiService.reviewClaim(achievement.id, action, recordId);
    setActingOn(null);
    fetchData();
  };

  const applicantMeta = (ap: Applicant) => {
    const parts =
      ap.mentorType === 'parent'
        ? ['Parent applicant', ap.occupation]
        : [
            ap.university,
            ap.classYear ? `'${String(ap.classYear).slice(-2)}` : null,
          ];
    parts.push(ap.email);
    parts.push(`applied ${new Date(ap.appliedAt).toLocaleDateString()}`);
    parts.push(`$${ap.pricePerSession}/hr`);
    return parts.filter(Boolean).join(' · ');
  };

  if (denied) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <div className="card" style={{ maxWidth: 520 }}>
            <h3 style={{ marginBottom: 8 }}>Admin access required</h3>
            <p className="meta">
              This page is for the internal verification &amp; supply ops team. Sign in with an
              admin account to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section" style={{ paddingTop: 40 }}>
      <div className="wrap">
        <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
          Internal · verification &amp; supply ops
        </span>
        <h1 style={{ fontSize: 30 }}>Mentor applications</h1>
        <p style={{ color: 'var(--ink-secondary)', marginTop: 8, maxWidth: '64ch' }}>
          Review each applicant's submitted proof documents and approve credentials one by one.
          Verified credentials show a verified sign on the mentor's public profile; anything
          unverified shows without one. Only contradictions are rejected.
        </p>

        <div className="stat-row" style={{ margin: '28px 0' }}>
          <div className="card">
            <div className="stat">
              <div className="num">{loading ? '—' : pendingCount}</div>
              <div className="lbl">Credentials pending review</div>
            </div>
          </div>
          <div className="card">
            <div className="stat">
              <div className="num plain">{loading ? '—' : applicants.length}</div>
              <div className="lbl">Applicants in the queue</div>
            </div>
          </div>
          <div className="card">
            <div className="stat">
              <div className="num plain">{loading ? '—' : waitlistTotal}</div>
              <div className="lbl">Families on the waitlist</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="card" style={{ marginBottom: 24 }}>
            <p className="meta">Loading applications…</p>
          </div>
        ) : applicants.length === 0 ? (
          <div className="card" style={{ marginBottom: 24 }}>
            <p className="meta">Queue is clear — no applications pending review.</p>
          </div>
        ) : (
          applicants.map((ap) => (
            <div className="card" key={ap.profileId} style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 16,
                  marginBottom: 14,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <h3 style={{ marginBottom: 2 }}>{ap.displayName}</h3>
                  <div className="meta">{applicantMeta(ap)}</div>
                </div>
                {ap.isVerified ? (
                  <Stamp variant="verified">Approved — live in marketplace</Stamp>
                ) : (
                  <Stamp variant="selfrep">Application in review</Stamp>
                )}
              </div>
              <table className="data">
                <thead>
                  <tr>
                    <th>Credential</th>
                    <th>Submitted proof</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ap.achievements.map((a) => {
                    const status = STATUS_META[a.verificationStatus] || STATUS_META.pending;
                    return (
                      <tr key={a.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>
                            {a.title}
                            {a.year ? ` (${a.year})` : ''}
                          </div>
                          <div className="meta">
                            {[
                              a.category,
                              a.rank,
                              a.activityTier
                                ? TIER_LABELS[a.activityTier] || a.activityTier
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </td>
                        <td>
                          {a.documents.length === 0 ? (
                            <span className="meta">No proof submitted</span>
                          ) : (
                            a.documents.map((d) => (
                              <div key={d.id}>
                                <a
                                  href={d.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: 'var(--cardinal)',
                                    fontWeight: 500,
                                    fontSize: 14,
                                  }}
                                >
                                  {d.fileName} ↗
                                </a>
                                <span className="meta">
                                  {' '}
                                  · {new Date(d.uploadedAt).toLocaleDateString()}
                                </span>
                              </div>
                            ))
                          )}
                        </td>
                        <td>
                          <Stamp variant={status.variant}>{status.label}</Stamp>
                          {a.verificationRecordId && (
                            <div className="meta" style={{ marginTop: 4 }}>
                              Record {a.verificationRecordId}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {a.verificationStatus === 'pending' ? (
                            <>
                              <button
                                className="btn btn-primary btn-sm"
                                disabled={actingOn === a.id}
                                onClick={() => handleAction(a, 'verify')}
                              >
                                Verify
                              </button>{' '}
                              <button
                                className="btn btn-secondary btn-sm"
                                disabled={actingOn === a.id}
                                onClick={() => handleAction(a, 'self_report')}
                              >
                                Self-reported
                              </button>{' '}
                              <button
                                className="btn btn-secondary btn-sm"
                                disabled={actingOn === a.id}
                                onClick={() => handleAction(a, 'reject')}
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <span className="meta">
                              Reviewed
                              {a.verifiedAt
                                ? ` ${new Date(a.verifiedAt).toLocaleDateString()}`
                                : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))
        )}

        <div className="card" style={{ maxWidth: 560, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 4 }}>Demand waitlist → recruiting targets</h3>
          <p className="meta" style={{ marginBottom: 14 }}>
            Supply is capped to demand so every mentor books within weeks.
          </p>
          {loading ? (
            <p className="meta">Loading…</p>
          ) : segments.length === 0 ? (
            <p className="meta">No open waitlist entries yet.</p>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th className="num-cell">Waiting</th>
                </tr>
              </thead>
              <tbody>
                {segments.map((s) => (
                  <tr key={s.segment}>
                    <td>{s.segment}</td>
                    <td className="num-cell">{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 4 }}>Open mentor requests</h3>
          <p className="meta" style={{ marginBottom: 14 }}>
            Submitted by families when the marketplace had no match — use these to decide who to
            recruit next.
          </p>
          {loading ? (
            <p className="meta">Loading…</p>
          ) : waitlistEntries.length === 0 ? (
            <p className="meta">No open requests yet.</p>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>Contact</th>
                  <th>Segment</th>
                  <th>What they're looking for</th>
                </tr>
              </thead>
              <tbody>
                {waitlistEntries.map((e) => (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(e.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      {e.user?.name ? (
                        <div style={{ fontWeight: 500 }}>{e.user.name}</div>
                      ) : null}
                      <a href={`mailto:${e.email}`} style={{ color: 'var(--cardinal)' }}>
                        {e.email}
                      </a>
                    </td>
                    <td>{e.segment}</td>
                    <td>
                      {e.note ? (
                        e.note
                      ) : (
                        <span className="meta">No details provided</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpsPage;
